/**
 * Timezones set from the "set interviewer timezone" modal, shared by every
 * surface that flags an interviewer without one (the scheduling wizard and the
 * stage-automation builder).
 *
 * Why a module store and not just the query cache: a chip's timezone is read
 * from the react-select option that was captured AT SELECTION TIME. An
 * interviewer picked ad-hoc from the async search is not in any list the page
 * can patch, so writing only to the query cache leaves that chip stale forever
 * — the bug this store exists to fix. Consumers resolve
 * `override ?? option.data.timezone` at render and subscribe via
 * `useInterviewerTimezoneOverrides`, so the stale snapshot stops mattering.
 *
 * The query cache is patched too (below), so a later read of an already-fetched
 * interviewer list sees the new value without a refetch.
 *
 * Module-store precedent: `src/api/calendarEvents/removedInterviewerCalendarCache.ts`.
 */
import moment from 'moment-timezone';
import type { QueryClient } from '@tanstack/react-query';
import type { InterviewersListResult } from '../interviewersList/fetchInterviewersList';

export type InterviewerTimezoneUpdate = { id: number; time_zone: string };

// A timezone counts as missing when it is absent OR not a zone moment knows —
// a legacy value like "IST" can't anchor a slot, so it is treated as unset.
// Mirrors the scheduling wizard's `formatClockFor` emptiness check.
export const isMissingTimezone = (timezone?: string | null): boolean =>
  !timezone || !moment.tz.zone(timezone);

const overrides = new Map<string, string>();
const subscribers = new Set<() => void>();
let version = 0;

const notify = (): void => {
  version += 1;
  subscribers.forEach((cb) => cb());
};

export const getInterviewerTimezoneVersion = (): number => version;

export const subscribeInterviewerTimezones = (cb: () => void): (() => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

export const getInterviewerTimezoneOverride = (
  id: number | string
): string | undefined => overrides.get(String(id));

// The timezone to render for an interviewer: anything set this session wins
// over whatever the option was loaded with.
export const resolveInterviewerTimezone = (
  id: number | string,
  fallback?: string | null
): string | undefined => overrides.get(String(id)) ?? fallback ?? undefined;

// Rewrites `time_zone` on every cached interviewer-list entry so a later read
// (a re-opened dropdown, another surface) sees the new value without a refetch.
// `setQueriesData` is deliberate: these entries have no active observers —
// everything goes through imperative `fetchQuery` — so `invalidateQueries`
// would mark them stale and refetch nothing.
const patchInterviewersListCache = (
  queryClient: QueryClient,
  byId: Map<string, string>
): void => {
  queryClient.setQueriesData<InterviewersListResult>(
    { queryKey: ['ats', 'interviewers_list'] },
    (previous) => {
      if (!previous) return previous;
      let changed = false;
      const patch = (rows: InterviewersListResult['pool']) =>
        rows.map((row) => {
          const next = byId.get(String(row.id));
          if (!next || row.time_zone === next) return row;
          changed = true;
          return { ...row, time_zone: next };
        });
      const pool = patch(previous.pool);
      const other = patch(previous.other);
      return changed ? { pool, other } : previous;
    }
  );
};

// Call once a save has actually succeeded. Records the overrides, patches the
// query cache, and wakes every subscribed chip.
export const applyInterviewerTimezoneUpdates = (
  queryClient: QueryClient,
  updates: InterviewerTimezoneUpdate[]
): void => {
  const applied = new Map<string, string>();
  updates.forEach(({ id, time_zone }) => {
    if (id === undefined || id === null || !time_zone) return;
    applied.set(String(id), time_zone);
  });
  if (applied.size === 0) return;

  applied.forEach((timezone, id) => overrides.set(id, timezone));
  patchInterviewersListCache(queryClient, applied);
  notify();
};

// Overrides are keyed by user id and outlive route changes, so clear them
// wherever the query cache is cleared (logout / account switch) and in tests.
export const clearInterviewerTimezoneOverrides = (): void => {
  if (overrides.size === 0) return;
  overrides.clear();
  notify();
};
